import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButtonGroup,
  ToggleButton,
  LinearProgress,
  Avatar,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Folder as FolderIcon,
  Image as ImageIcon,
  ArrowUpward as UpIcon,
  DragIndicator as DragIcon,
  CloudUpload as UploadIcon,
  Functions as FunctionsIcon,
} from '@mui/icons-material';
import MathText from '../../components/MathText';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { toast } from 'react-toastify';
import imageService from '../../services/image.service';
import imageContainerService from '../../services/imageContainer.service';
import { registerBeforeTabChange, unregisterBeforeTabChange } from './Workshop';
import ThumbnailPicker from '../../components/ThumbnailPicker';

interface ImagesProps {
  urlContainerId?: number | null;
  onNavChange?: (containerId: number | null, itemId: number | null, label?: string) => void;
}

const Images: React.FC<ImagesProps> = ({ urlContainerId, onNavChange }) => {
  const [images, setImages] = useState<any[]>([]);
  const [containers, setContainers] = useState<any[]>([]);
  const [selectedImage, setSelectedImage] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentContainer, setCurrentContainer] = useState<number | null>(null);
  const parentContainer = null; // Parent container is always null at root level

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // File input ref
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Ref to track current values for saving on unmount
  const currentImageRef = useRef<{
    image: any | null;
    description: string;
  }>({
    image: null,
    description: ''
  });

  // Update ref whenever values change
  useEffect(() => {
    currentImageRef.current = {
      image: selectedImage,
      description
    };
  }, [selectedImage, description]);

  // KaTeX state
  const [katexFormula, setKatexFormula] = useState('');
  const [textColor, setTextColor] = useState('#000000');
  const [bgColor, setBgColor] = useState('#ffffff');
  const katexPreviewRef = useRef<HTMLDivElement>(null);

  // Dialogs
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [createType, setCreateType] = useState<'container' | 'image' | 'katex'>('image');
  const [newContainerTitle, setNewContainerTitle] = useState('');

  // Edit container dialog
  const [editContainerDialogOpen, setEditContainerDialogOpen] = useState(false);
  const [editingContainer, setEditingContainer] = useState<any | null>(null);
  const [editContainerTitle, setEditContainerTitle] = useState('');
  const [editContainerThumbnail, setEditContainerThumbnail] = useState<number | null>(null);

  // Edit image dialog
  const [editImageDialogOpen, setEditImageDialogOpen] = useState(false);
  const [editingImage, setEditingImage] = useState<any | null>(null);
  const [editImageDescription, setEditImageDescription] = useState('');

  // Sync from URL (back/forward navigation)
  useEffect(() => {
    if (urlContainerId !== undefined && urlContainerId !== currentContainer) {
      setCurrentContainer(urlContainerId);
    }
  }, [urlContainerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const initialLoadDone = useRef(false);

  const loadImages = async () => {
    try {
      if (!initialLoadDone.current) setLoading(true);
      const containerParam = currentContainer === null ? 'null' : currentContainer;
      const data = await imageService.getAll({ container: containerParam });
      setImages(data);
    } catch (error) {
      console.error('Error loading images:', error);
      toast.error('Erreur lors du chargement des images');
    } finally {
      setLoading(false);
      initialLoadDone.current = true;
    }
  };

  const loadContainers = async () => {
    try {
      const parentParam = currentContainer === null ? 'null' : currentContainer;
      const data = await imageContainerService.getAll({ parent: parentParam });
      setContainers(data);
    } catch (error) {
      console.error('Error loading containers:', error);
      toast.error('Erreur lors du chargement des contenants');
    }
  };

  useEffect(() => {
    loadImages();
    loadContainers();
  }, [currentContainer]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (imagePreviewUrl && imagePreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
    };
  }, [imagePreviewUrl]);

  // Auto-save description with 2 second delay
  useEffect(() => {
    if (!selectedImage || !title) return;

    const timeoutId = setTimeout(async () => {
      try {
        await imageService.update(selectedImage.id, { description });

        // Update selected image
        const updatedImage = { ...selectedImage, description };
        setSelectedImage(updatedImage);

        // Update image in the list
        setImages(imgs => imgs.map(img => img.id === selectedImage.id ? updatedImage : img));

        console.log('Image description auto-saved');
      } catch (error) {
        console.error('Error auto-saving image description:', error);
      }
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [description, selectedImage, title]);

  // Register callback to save before tab change
  useEffect(() => {
    const saveBeforeTabChange = async () => {
      const current = currentImageRef.current;
      if (current.image && current.image.description !== current.description) {
        try {
          await imageService.update(current.image.id, { description: current.description });
          console.log('Image saved before tab change');
        } catch (error) {
          console.error('Error saving image before tab change:', error);
        }
      }
    };

    registerBeforeTabChange('images', saveBeforeTabChange);

    return () => {
      unregisterBeforeTabChange('images');
    };
  }, []); // Empty dependency array - only register/unregister on mount/unmount

  // Save on component unmount (for navigation outside Workshop)
  useEffect(() => {
    return () => {
      const current = currentImageRef.current;
      if (current.image && current.image.description !== current.description) {
        // Fire off the save request without awaiting
        // The browser will attempt to complete it even during navigation
        imageService.update(current.image.id, { description: current.description })
          .then(() => console.log('Image saved on unmount'))
          .catch(err => console.error('Error saving image on unmount:', err));
      }
    };
  }, []); // Empty dependency array - only run on unmount

  const handleSelectImage = async (image: any) => {
    // Save current image before switching
    if (selectedImage && title && description !== selectedImage.description) {
      try {
        await imageService.update(selectedImage.id, { description });

        // Update selected image
        const updatedImage = { ...selectedImage, description };
        setSelectedImage(updatedImage);

        // Update image in the list
        setImages(imgs => imgs.map(img => img.id === selectedImage.id ? updatedImage : img));

        console.log('Saved image before switching');
      } catch (error) {
        console.error('Error saving image:', error);
      }
    }

    setSelectedImage(image);
    setTitle(image.title);
    setDescription(image.description || '');
    setImageFile(null);
    setImagePreviewUrl('');
  };

  const handleNavigateToContainer = async (containerId: number | null) => {
    // Save current image before navigating
    if (selectedImage && title && description !== selectedImage.description) {
      try {
        await imageService.update(selectedImage.id, { description });

        // Update selected image
        const updatedImage = { ...selectedImage, description };
        setSelectedImage(updatedImage);

        // Update image in the list
        setImages(imgs => imgs.map(img => img.id === selectedImage.id ? updatedImage : img));

        console.log('Saved image before navigating');
      } catch (error) {
        console.error('Error saving image:', error);
      }
    }

    setCurrentContainer(containerId);
    const containerLabel = containerId !== null ? containers.find(c => c.id === containerId)?.title : undefined;
    onNavChange?.(containerId, null, containerLabel);
  };

  const handleFileSelect = (file: File) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner un fichier image valide');
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('La taille du fichier ne doit pas dépasser 10MB');
      return;
    }

    setImageFile(file);

    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setImagePreviewUrl(previewUrl);

    // Set title from filename if not already set
    if (!title) {
      const fileName = file.name.replace(/\.[^/.]+$/, ''); // Remove extension
      setTitle(fileName);
    }

    toast.success(`Fichier sélectionné: ${file.name}`);
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);

    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error('Le titre est requis');
      return;
    }

    if (!imageFile && !selectedImage) {
      toast.error('Veuillez sélectionner un fichier image');
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      if (selectedImage) {
        // Update existing image
        if (imageFile) {
          // If new file is uploaded, send as FormData
          const formData = new FormData();
          formData.append('title', title);
          formData.append('image_file', imageFile);
          if (currentContainer !== null) {
            formData.append('container', currentContainer.toString());
          }
          await imageService.update(selectedImage.id, formData);
        } else {
          // Just update title (description is edited in the right panel)
          await imageService.update(selectedImage.id, {
            title,
            container: currentContainer,
          });
        }
        toast.success('Image mise à jour');
        // Reload to get updated data
        loadImages();
      } else {
        // Create new image
        if (!imageFile) {
          toast.error('Veuillez sélectionner un fichier image');
          return;
        }

        const formData = new FormData();
        formData.append('title', title);
        formData.append('description', description);
        formData.append('image_file', imageFile);

        // Simulate upload progress
        setUploadProgress(50);

        if (currentContainer !== null) {
          formData.append('container', currentContainer.toString());
        }

        await imageService.create(formData);
        setUploadProgress(100);
        toast.success('Image créée avec succès');
      }

      resetForm();
      loadImages();
    } catch (error) {
      console.error('Error saving image:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (imageId: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette image ?')) {
      return;
    }

    try {
      await imageService.delete(imageId);
      toast.success('Image supprimée');
      resetForm();
      loadImages();
    } catch (error) {
      console.error('Error deleting image:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleTogglePublicImage = async (imageId: number, newIsPublic: boolean) => {
    try {
      await imageService.update(imageId, { is_public: newIsPublic });
      toast.success(`Image rendue ${newIsPublic ? 'publique' : 'privée'}`);

      // Update in the list
      setImages(images.map(img => img.id === imageId ? { ...img, is_public: newIsPublic } : img));

      // Update selected image if it's the same
      if (selectedImage?.id === imageId) {
        setSelectedImage({ ...selectedImage, is_public: newIsPublic });
      }
    } catch (error) {
      console.error('Error updating image visibility:', error);
      toast.error('Erreur lors de la mise à jour de la visibilité');
    }
  };

  const handleNew = () => {
    resetForm(); // Start with clean form
    setKatexFormula('');
    setTextColor('#000000');
    setBgColor('#ffffff');
    setImageDialogOpen(true);
    setCreateType('image'); // Default to image creation
  };

  const handleCreateKatexImage = async () => {
    if (!title.trim() || !katexFormula.trim() || !katexPreviewRef.current) return;

    try {
      setUploading(true);
      const { toPng } = await import('html-to-image');
      const dataUrl = await toPng(katexPreviewRef.current, { pixelRatio: 3, skipFonts: true });

      // Convert data URL to blob
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      const file = new File([blob], `${title.replace(/[^a-zA-Z0-9]/g, '_')}.png`, { type: 'image/png' });

      const formData = new FormData();
      formData.append('title', title);
      formData.append('image_file', file);
      if (currentContainer !== null) {
        formData.append('container', currentContainer.toString());
      }

      await imageService.create(formData);
      toast.success('Image KaTeX créée avec succès');
      setImageDialogOpen(false);
      resetForm();
      setKatexFormula('');
      loadImages();
    } catch (error) {
      console.error('Error creating KaTeX image:', error);
      toast.error("Erreur lors de la création de l'image KaTeX");
    } finally {
      setUploading(false);
    }
  };

  const handleCreateContainer = async () => {
    if (!newContainerTitle.trim()) {
      toast.error('Le nom du contenant est requis');
      return;
    }

    try {
      await imageContainerService.create({
        title: newContainerTitle,
        parent: currentContainer,
      });
      toast.success('Contenant créé');
      setImageDialogOpen(false);
      setNewContainerTitle('');
      loadContainers();
    } catch (error) {
      console.error('Error creating container:', error);
      toast.error('Erreur lors de la création du contenant');
    }
  };

  const handleEditContainer = (container: any) => {
    setEditingContainer(container);
    setEditContainerTitle(container.title);
    setEditContainerThumbnail(container.thumbnail || null);
    setEditContainerDialogOpen(true);
  };

  const handleUpdateContainer = async () => {
    if (!editContainerTitle.trim()) {
      toast.error('Le nom du contenant est requis');
      return;
    }

    try {
      await imageContainerService.update(editingContainer.id, {
        title: editContainerTitle,
        thumbnail: editContainerThumbnail,
      } as any);
      toast.success('Contenant mis à jour');
      setEditContainerDialogOpen(false);
      setEditingContainer(null);
      setEditContainerTitle('');
      setEditContainerThumbnail(null);
      loadContainers();
    } catch (error) {
      console.error('Error updating container:', error);
      toast.error('Erreur lors de la mise à jour du contenant');
    }
  };

  const handleEditImage = (image: any) => {
    setEditingImage(image);
    setEditImageDescription(image.description || '');
    setEditImageDialogOpen(true);
  };

  const handleUpdateImageDescription = async () => {
    try {
      await imageService.update(editingImage.id, {
        description: editImageDescription,
      });
      toast.success('Image mise à jour');
      loadImages();
      setEditImageDialogOpen(false);
      setEditingImage(null);
      setEditImageDescription('');

      // Update selected image if it's the one being edited
      if (selectedImage?.id === editingImage.id) {
        setSelectedImage({ ...selectedImage, description: editImageDescription });
        setDescription(editImageDescription);
      }
    } catch (error) {
      console.error('Error updating image:', error);
      toast.error('Erreur lors de la mise à jour de l\'image');
    }
  };

  const handleDeleteContainer = async (containerId: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce contenant ?')) {
      return;
    }

    try {
      await imageContainerService.delete(containerId);
      toast.success('Contenant supprimé');
      loadContainers();
    } catch (error) {
      console.error('Error deleting container:', error);
      toast.error('Erreur lors de la suppression du contenant');
    }
  };

  const handleDragEnd = async (result: any) => {
    const { source, destination, draggableId } = result;

    // If no destination, do nothing
    if (!destination) return;

    const isImage = draggableId.startsWith('image-');

    // Check if we're dropping a image onto the parent (..)
    if (isImage && destination.droppableId === 'parent-drop') {
      const imageId = parseInt(draggableId.replace('image-', ''));

      try {
        await imageService.update(imageId, { container: parentContainer });
        toast.success('Image déplacée vers le niveau parent');
        loadImages();
        loadContainers();
      } catch (error) {
        console.error('Error moving image to parent:', error);
        toast.error('Erreur lors du déplacement');
      }
      return;
    }

    // Check if we're dropping a image onto a container
    if (isImage && destination.droppableId.startsWith('container-drop-')) {
      const imageId = parseInt(draggableId.replace('image-', ''));
      const containerId = parseInt(destination.droppableId.replace('container-drop-', ''));

      try {
        await imageService.update(imageId, { container: containerId });
        toast.success('Image déplacée dans le contenant');
        loadImages();
        loadContainers();
      } catch (error) {
        console.error('Error moving image to container:', error);
        toast.error('Erreur lors du déplacement');
      }
      return;
    }

    // If dropped in the same position, do nothing
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }

    // Reorder images within the same list
    if (isImage && source.droppableId === 'images' && destination.droppableId === 'images') {
      const items = Array.from(images);
      const [reorderedItem] = items.splice(source.index, 1);
      items.splice(destination.index, 0, reorderedItem);

      // Update order property
      const updatedItems = items.map((item, index) => ({
        ...item,
        order: index,
      }));

      setImages(updatedItems);

      try {
        await imageService.reorder(updatedItems.map(item => ({ id: item.id, order: item.order })));
      } catch (error) {
        console.error('Error reordering images:', error);
        toast.error('Erreur lors de la réorganisation');
        loadImages(); // Reload to revert
      }
    }
  };

  const resetForm = () => {
    setSelectedImage(null);
    setTitle('');
    setDescription('');
    setImageFile(null);
    setImagePreviewUrl('');
    setUploadProgress(0);
    setUploading(false);

    // Revoke object URL to prevent memory leaks
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
    }
  };

  return (
    <Box>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Grid container spacing={3}>
          {/* Left Panel - Images List */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, height: 'calc(100vh - 250px)', overflow: 'auto' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Images</Typography>
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleNew}
                  size="small"
                >
                  Nouveau
                </Button>
              </Box>

              {/* Parent navigation */}
              {currentContainer !== null && (
                <Droppable droppableId="parent-drop" type="IMAGE">
                  {(provided, snapshot) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      <Card
                        sx={{
                          mb: 1,
                          cursor: 'pointer',
                          backgroundColor: snapshot.isDraggingOver ? 'warning.light' : 'action.hover',
                          border: snapshot.isDraggingOver ? '2px solid' : 'none',
                          borderColor: snapshot.isDraggingOver ? 'warning.main' : 'transparent',
                          transition: 'all 0.2s',
                        }}
                        onClick={() => handleNavigateToContainer(parentContainer)}
                      >
                        <CardContent sx={{ py: 1 }}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <UpIcon color="action" />
                            <Typography variant="body2" fontWeight="bold">..</Typography>
                          </Box>
                        </CardContent>
                      </Card>
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              )}

              {/* Containers */}
              {containers.map((container, containerIndex) => (
                <Droppable key={`container-droppable-${container.id}`} droppableId={`container-drop-${container.id}`} type="IMAGE">
                  {(provided, snapshot) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      <Card
                        sx={{
                          mb: 1,
                          cursor: 'pointer',
                          border: '2px dashed',
                          borderColor: snapshot.isDraggingOver ? 'success.main' : 'primary.main',
                          backgroundColor: snapshot.isDraggingOver ? 'success.light' : 'inherit',
                          transition: 'all 0.2s',
                        }}
                        onClick={() => handleNavigateToContainer(container.id)}
                      >
                        <CardContent sx={{ py: 1 }}>
                          <Box display="flex" alignItems="center" gap={1}>
                            {container.thumbnail_url ? (
                              <Avatar
                                src={container.thumbnail_url}
                                alt={container.title}
                                variant="rounded"
                                sx={{ width: 40, height: 40 }}
                              />
                            ) : (
                              <FolderIcon color="primary" />
                            )}
                            <Typography variant="body2" sx={{ flex: 1 }}>
                              {container.title}
                            </Typography>
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEditContainer(container); }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteContainer(container.id); }} sx={{ color: 'error.main' }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </CardContent>
                      </Card>
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              ))}

              {/* Images */}
              {loading ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </Box>
              ) : (
                <Droppable droppableId="images" type="IMAGE">
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {images.map((image, imageIndex) => (
                        <Draggable key={`image-${image.id}`} draggableId={`image-${image.id}`} index={imageIndex}>
                          {(provided, snapshot) => (
                            <Card
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              sx={{
                                mb: 1,
                                cursor: 'pointer',
                                backgroundColor: selectedImage?.id === image.id ? 'action.selected' : snapshot.isDragging ? 'action.hover' : image.is_public ? 'rgba(33, 150, 243, 0.08)' : 'inherit',
                              }}
                              onClick={() => handleSelectImage(image)}
                            >
                              <CardContent sx={{ py: 1 }}>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Box {...provided.dragHandleProps}>
                                    <DragIcon sx={{ cursor: 'grab', color: 'action.active', fontSize: 20 }} />
                                  </Box>
                                  {image.thumbnail_url ? (
                                    <Avatar
                                      src={image.thumbnail_url}
                                      alt={image.title}
                                      variant="rounded"
                                      sx={{ width: 40, height: 40 }}
                                    />
                                  ) : (
                                    <Avatar variant="rounded" sx={{ width: 40, height: 40, bgcolor: 'action.hover' }}>
                                      <ImageIcon sx={{ fontSize: 20 }} />
                                    </Avatar>
                                  )}
                                  <Typography variant="body2" sx={{ flex: 1 }}>
                                    {image.title}
                                  </Typography>
                                  <Switch
                                    size="small"
                                    checked={image.is_public || false}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      handleTogglePublicImage(image.id, e.target.checked);
                                    }}
                                    sx={{ ml: 0.5 }}
                                  />
                                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEditImage(image); }}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(image.id); }} sx={{ color: 'error.main' }}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </CardContent>
                            </Card>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              )}

              {images.length === 0 && !loading && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                  Aucune image disponible
                </Typography>
              )}
            </Paper>
          </Grid>

          {/* Right Panel - Image Preview */}
          <Grid item xs={12} md={8}>
            <Paper sx={{
              p: 2,
              height: 'calc(100vh - 250px)',
              overflow: 'auto',
              backgroundColor: selectedImage?.is_public ? 'rgba(33, 150, 243, 0.08)' : 'background.paper',
              borderRadius: 2
            }}>
              {selectedImage ? (
                <>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">
                      {selectedImage.title}
                    </Typography>
                    <Box>
                      <IconButton onClick={() => handleEditImage(selectedImage)}>
                        <EditIcon />
                      </IconButton>
                    </Box>
                  </Box>

                  <TextField
                    fullWidth
                    label="Description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    margin="normal"
                    multiline
                    rows={4}
                  />

                  {selectedImage.image_file_url && (
                    <Box mb={3}>
                      <Typography variant="subtitle1" gutterBottom>
                        Image
                      </Typography>
                      <Box
                        sx={{
                          position: 'relative',
                          paddingTop: '56.25%', // 16:9 aspect ratio
                          backgroundColor: 'action.hover',
                          borderRadius: 1,
                        }}
                      >
                        <img
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            borderRadius: 4,
                            objectFit: 'contain',
                          }}
                          src={selectedImage.image_file_url}
                          alt={selectedImage.title}
                        />
                      </Box>
                    </Box>
                  )}

                </>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    color: 'text.secondary'
                  }}
                >
                  <ImageIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
                  <Typography variant="h6" gutterBottom>
                    Aucune image sélectionnée
                  </Typography>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    Sélectionnez une image dans la liste ou cliquez sur "Nouveau" pour en ajouter une
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </DragDropContext>

      {/* Creation Dialog */}
      <Dialog
        open={imageDialogOpen}
        onClose={() => {
          setImageDialogOpen(false);
          setNewContainerTitle('');
          if (!selectedImage) {
            resetForm();
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{selectedImage ? 'Modifier l\'image' : 'Nouveau'}</DialogTitle>
        <DialogContent>
          {!selectedImage && (
            <Box sx={{ mb: 2, mt: 1 }}>
              <ToggleButtonGroup
                value={createType}
                exclusive
                onChange={(e, newValue) => newValue && setCreateType(newValue)}
                fullWidth
              >
                <ToggleButton value="container">
                  <FolderIcon sx={{ mr: 1 }} />
                  Contenant
                </ToggleButton>
                <ToggleButton value="image">
                  <ImageIcon sx={{ mr: 1 }} />
                  Image
                </ToggleButton>
                <ToggleButton value="katex">
                  <FunctionsIcon sx={{ mr: 1 }} />
                  KaTeX
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}

          {createType === 'container' && (
            <TextField
              autoFocus
              margin="dense"
              label="Nom du contenant"
              fullWidth
              variant="outlined"
              value={newContainerTitle}
              onChange={(e) => setNewContainerTitle(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleCreateContainer();
                }
              }}
            />
          )}

          {createType === 'katex' && (
            <Box>
              <TextField
                fullWidth
                label="Titre"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Formule KaTeX"
                value={katexFormula}
                onChange={(e) => setKatexFormula(e.target.value)}
                margin="normal"
                placeholder="Ex: x^2 + y^2 = r^2"
                multiline
                rows={2}
              />
              <Box display="flex" gap={3} mt={2} alignItems="center">
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2">Couleur du texte</Typography>
                  <input
                    type="color"
                    value={textColor}
                    onChange={(e) => setTextColor(e.target.value)}
                    style={{ width: 40, height: 32, border: 'none', cursor: 'pointer' }}
                  />
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2">Couleur du fond</Typography>
                  <input
                    type="color"
                    value={bgColor}
                    onChange={(e) => setBgColor(e.target.value)}
                    style={{ width: 40, height: 32, border: 'none', cursor: 'pointer' }}
                  />
                </Box>
              </Box>
              {katexFormula && (
                <Box mt={3}>
                  <Typography variant="subtitle1" gutterBottom>Aperçu</Typography>
                  <Box
                    ref={katexPreviewRef}
                    sx={{
                      backgroundColor: bgColor,
                      color: textColor,
                      p: 3,
                      borderRadius: 1,
                      display: 'inline-block',
                      fontSize: '2rem',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <MathText text={katexFormula} format="tex" />
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {createType === 'image' && (
            <Box>
              <TextField
                fullWidth
                label="Titre"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                margin="normal"
                required
              />

              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleFileInputChange}
              />

              {/* Drag and drop zone or URL input */}
              {!imageFile && !imagePreviewUrl ? (
                <>
                  <Box
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    sx={{
                      border: '2px dashed',
                      borderColor: isDragging ? 'primary.main' : 'grey.400',
                      borderRadius: 2,
                      p: 3,
                      mt: 2,
                      textAlign: 'center',
                      backgroundColor: isDragging ? 'action.hover' : 'transparent',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: 'action.hover',
                      },
                    }}
                    onClick={handleBrowseClick}
                  >
                    <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                    <Typography variant="h6" gutterBottom>
                      Glissez-déposez votre image ici
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      ou
                    </Typography>
                    <Button variant="outlined" sx={{ mt: 1 }}>
                      Parcourir les fichiers
                    </Button>
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 2 }}>
                      Formats acceptés: JPG, PNG, GIF, WEBP (max 10MB)
                    </Typography>
                  </Box>
                </>
              ) : (
                <Box sx={{ mt: 2 }}>
                  {imageFile && (
                    <Card sx={{ backgroundColor: 'action.hover', mb: 2 }}>
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                          <ImageIcon color="primary" />
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            {imageFile.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {(imageFile.size / (1024 * 1024)).toFixed(2)} MB
                          </Typography>
                          <IconButton size="small" onClick={() => {
                            setImageFile(null);
                            setImagePreviewUrl('');
                            if (imagePreviewUrl) {
                              URL.revokeObjectURL(imagePreviewUrl);
                            }
                          }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        {uploading && (
                          <Box sx={{ width: '100%' }}>
                            <LinearProgress variant="determinate" value={uploadProgress} />
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                              Upload en cours: {uploadProgress}%
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </Box>
              )}

              {/* Description field only for new images, not for editing */}
              {!selectedImage && (
                <TextField
                  fullWidth
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  margin="normal"
                  multiline
                  rows={4}
                />
              )}

              {imagePreviewUrl && (
                <Box mt={3}>
                  <Typography variant="subtitle1" gutterBottom>
                    Aperçu
                  </Typography>
                  <Box
                    sx={{
                      position: 'relative',
                      paddingTop: '56.25%', // 16:9 aspect ratio
                      backgroundColor: 'action.hover',
                      borderRadius: 1,
                    }}
                  >
                    <img
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        borderRadius: 4,
                        objectFit: 'contain',
                      }}
                      src={imagePreviewUrl}
                      alt="Aperçu"
                    />
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setImageDialogOpen(false);
              setNewContainerTitle('');
              if (!selectedImage) {
                resetForm();
              }
            }}
            disabled={uploading}
          >
            Annuler
          </Button>
          {createType === 'container' && (
            <Button variant="contained" onClick={handleCreateContainer}>Créer</Button>
          )}
          {createType === 'katex' && (
            <Button
              variant="contained"
              onClick={handleCreateKatexImage}
              disabled={!title.trim() || !katexFormula.trim() || uploading}
            >
              {uploading ? 'Génération...' : 'Créer'}
            </Button>
          )}
          {createType === 'image' && (
            <Button
              variant="contained"
              onClick={async () => {
                await handleSave();
                setImageDialogOpen(false);
              }}
              disabled={!title.trim() || (!imageFile && !selectedImage) || uploading}
            >
              {uploading ? 'Upload en cours...' : (selectedImage ? 'Mettre à jour' : 'Créer')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Edit Container Dialog */}
      <Dialog open={editContainerDialogOpen} onClose={() => setEditContainerDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Modifier le contenant</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nom du contenant"
            fullWidth
            variant="outlined"
            value={editContainerTitle}
            onChange={(e) => setEditContainerTitle(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && editContainerTitle.trim()) {
                handleUpdateContainer();
              }
            }}
          />

          <ThumbnailPicker
            value={editContainerThumbnail}
            onChange={setEditContainerThumbnail}
            currentThumbnailUrl={editingContainer?.thumbnail_url}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditContainerDialogOpen(false);
            setEditingContainer(null);
            setEditContainerTitle('');
            setEditContainerThumbnail(null);
          }}>Annuler</Button>
          <Button variant="contained" onClick={handleUpdateContainer}>Modifier</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Image Dialog */}
      <Dialog open={editImageDialogOpen} onClose={() => setEditImageDialogOpen(false)}>
        <DialogTitle>Modifier l'image</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Description de l'image"
            fullWidth
            variant="outlined"
            multiline
            rows={4}
            value={editImageDescription}
            onChange={(e) => setEditImageDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditImageDialogOpen(false);
            setEditingImage(null);
            setEditImageDescription('');
          }}>Annuler</Button>
          <Button variant="contained" onClick={handleUpdateImageDescription}>Modifier</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Images;
